"use client";

import { Toast } from "@fanduel/formation-react-components";
import { Fragment, useMemo, useState, useEffect } from "react";
import ErrorBoundary from "@/components/empty/ErrorBoundary";
import ErrorComponent from "@/components/empty/ErrorComponent";
import NflMatchupCard from "@/components/nflSchedules/NflMatchupCard";
import {
  SCHEDULE_GAME_CARD_EXPANDED,
  SCHEDULE_PAGE_VIEWED,
} from "@/constants/amplitude";
import { SCREENS } from "@/constants/breakpoints";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useMedia from "@/utils/hooks/useMedia";
import useOnce from "@/utils/hooks/useOnce";
import { useScheduledGamesFilter } from "@/utils/hooks/useScheduledGamesFilter";
import DateHeading from "../DateHeading";
import styles from "./NflSchedulesClient.module.scss";

interface NflScheduleClientProps {
  week: number | undefined;
}

const NflScheduleClientBase = ({ week }: NflScheduleClientProps) => {
  const isTooSmallForFullNames = useMedia(`(max-width: 1320px)`);
  const isTablet = useMedia(`(max-width: 1120px`);
  const isMobile = useMedia(`(max-width: ${SCREENS.MD})`);
  const { logAmplitudeEvent } = useAmplitude();

  const filter = useScheduledGamesFilter();

  const games = filter?.games || [];

  useOnce(() => {
    logAmplitudeEvent(SCHEDULE_PAGE_VIEWED, {
      Competition: "NFL",
      "Page Name": "schedule",
    });
  });
  const sortBy = filter?.sortBy || "";

  const initialState = useMemo(
    () => Array(games.length).fill(false),
    [games.length]
  );
  const [expandedCards, setExpandedCards] = useState(initialState);

  useEffect(() => {
    setExpandedCards(initialState);
  }, [sortBy, initialState]);

  const handleExpand = (index: number) => {
    const newExpandedCards = [...expandedCards];
    newExpandedCards[index] = !newExpandedCards[index];
    setExpandedCards(newExpandedCards);
    logAmplitudeEvent(SCHEDULE_GAME_CARD_EXPANDED, {
      "Event Name":
        games[index]?.awayTeam?.name + " @ " + games[index]?.homeTeam?.name,
      "Event Start Date": games[index]?.scheduledTime ?? "",
      Competitions: "NFL",
      Position: index,
      Module: "game_card",
      "Page Name": "schedule",
    });
  };

  if (games.length === 0) {
    return (
      <ErrorComponent
        headline="Aaand... no good!"
        subtext={`No games are currently set for ${week ? `week ${week}` : "this week"}. Please check back later.`}
      />
    );
  }

  return (
    <div className={styles.scheduleContainer}>
      {filter?.isError ? (
        <Toast
          className={styles.toast}
          intent="important"
          title="Connection lost!"
        >
          Trying to reconnect...
        </Toast>
      ) : null}
      {games?.map((game, index) => {
        if (game) {
          return (
            <Fragment
              key={`${game.scheduledTime}${game.awayTeam?.code}@${game.homeTeam?.code}`}
            >
              <DateHeading
                currentGameTime={game.scheduledTime}
                previousGameTime={
                  index > 0 ? games[index - 1]?.scheduledTime : null
                }
              />
              <NflMatchupCard
                game={game}
                expanded={expandedCards[index]}
                handleExpand={handleExpand}
                index={index}
                isMobile={isMobile}
                isTablet={isTablet}
                isTooSmallForFullNames={isTooSmallForFullNames}
              />
            </Fragment>
          );
        }
      })}
    </div>
  );
};
const NflScheduleClient = (props: NflScheduleClientProps) => (
  <ErrorBoundary>
    <NflScheduleClientBase {...props} />
  </ErrorBoundary>
);

export default NflScheduleClient;
